<template>
  <div @click.stop="() => {}" class="base-date-picker__date_wrapper">
    <slot></slot>
    <template v-if="bigDatePicker">
      <button class="base-date-picker__title"
              :style="titleStyle"
              @click.stop="openPicker"
              :class="{
                'base-date-picker__date-button_disabled': disabled,
                'base-date-picker__title_border-less': borderLess,
              }">
        {{date}}
        <div v-if="!withoutArrow" class="base-date-picker__arrow" />
      </button>
    </template>
    <template v-else>
      <span v-if="isDateAssigned"
              class="base-date-picker__date-button"
              @click.stop="openPicker"
              :class="{'base-date-picker__date-button_disabled': disabled}">
        {{date}}
      </span>
      <base-material-design-icon v-else icon="calendar"
                                 class="base-date-picker__date-button base-date-picker__date-button_icon"
                                 @click.stop="openPicker"
                                 :class="{'base-date-picker__date-button_highlighted': pickerState.visibility === 'visible',
                                                 'base-date-picker__date-button_disabled': disabled}" />
    </template>
    <v-date-picker class="base-date-picker__date-picker"
                   :class="{'base-date-picker__available-date-picker': bigDatePicker}"
                   ref='picker'
                   :value="modelValue"
                   :popover="pickerState"
                   :attributes="attributes"
                   @input="setNewDate"
                   :available-dates="mapAvailableDates"
                   :min-date="min"
                   :max-date="max"
                   :input-props="{
                                    class: 'hidden',
                                    readonly: true
                                  }" />
  </div>
</template>

<script>
import filters from '@/common/Filters'
import moment from 'moment'
import _ from 'lodash'
import CreateEventPopup from "../features/calendar/CreateEventPopup";
/**
 * Picker for several popups like {@link CreateEventPopup}
 */
export default {
  name: 'BaseDatePickerButton',
  props: {
    modelValue: {
      default: null
    },
    // set most recent data on clear button
    mostRecentOnClear: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    availableDates: {
      type: Array,
      default: () => []
    },
    minDate: {
      default: null
    },
    maxDate: {
      default: null
    },
    customPickerState: {
      type: Object,
      default: () => ({})
    },
    titleStyle: {
      type: Object,
      default: () => ({})
    },
    removeCaret: {
      type: Boolean,
      default: false
    },
    withoutArrow: {
      type: Boolean,
      default: false
    },
    borderLess: {
      type: Boolean,
      default: false
    },
    // prop to show big place for enable date picker
    bigDatePicker: {
      type: Boolean,
      default: false
    },
    // show with bigDatePicker if no date selected
    title: {
      type: String,
      default: null
    },
    // set last date with bigDatePicker on init
    setLastDate: {
      type: Boolean,
      default: false
    },
    dates: {
      type: Array,
      default: () => []
    }
  },
  model: {
    prop: 'modelValue',
    event: 'change'
  },
  data () {
    return {
      /* we need to use pickerState object 'cause calendar popup opens by click on button */
      pickerState: {
        visibility: 'focus',
        placement: 'top'
      },
      isDatePickerOpened: false,
      handler: null
    }
  },
  created () {
    if (this.bigDatePicker && this.setLastDate) {
      this.setNewDate(this.availableDates[0])
    }
    if (this.customPickerState && Object.entries(this.customPickerState).length) {
      Object.assign(this.pickerState, this.customPickerState)
    }
    this.handler = this.clickHandler.bind(this)
    document.addEventListener('click', this.handler)
  },
  destroyed () {
    document.removeEventListener('click', this.handler)
  },
  computed: {
    date () {
      if (this.modelValue && this.modelValue !== 'clear') {
        const date = moment(this.convertDateForSafary(this.modelValue))
        return date.isValid() ? date.format('MMM DD, YYYY') : ''
      }
      if (this.title && this.bigDatePicker) {
        return this.title
      }
      if (this.bigDatePicker) {
        return 'Choose date'
      }
      return null
    },
    /**
     * select attribute settings {@link https://vcalendar.io/datepicker.html#selection-popover}
     * */
    attributes () {
      return [{
        key: 'snapshots',
        bar: true,
        dates: _.cloneDeep(this.dates)
      }]
    },
    min () {
      if (this.minDate === 'clear') {
        return null
      }
      return this.convertDateForSafary(this.minDate)
    },
    max () {
      if (this.maxDate === 'clear'){
        return null
      }
      return this.convertDateForSafary(this.maxDate)
    },
    isDateAssigned () {
      return this.modelValue && this.modelValue !== 'clear' && moment(this.modelValue).isValid()
    },
    mapAvailableDates () {
      if (!this.bigDatePicker) {
        return []
      }
      return this.availableDates.map(date => { return { start: date, end: date } })
    }
  },
  methods: {
    /**
     * handler for closing manually opened picker
     * @param event
     */
    clickHandler (event) {
      if (!this.isDatePickerOpened) {
        return
      }
      const classList = Array.from(_.get(event, 'target.classList', []))
      const insideDatePicker = classList.some(className => className.startsWith('vc-'))
      if (insideDatePicker) {
        return
      }
      this.pickerState.visibility = 'focus'
    },
    /**
     * control visibility state of picker manually
     */
    toggleVisibilityState () {
      this.pickerState.visibility = this.pickerState.visibility === 'focus' ? 'visible' : 'focus'
    },
    setNewDate (date) {
      this.$set(this.pickerState, 'visibility', 'focus')
      this.isDatePickerOpened = false
      this.modelValue = date
      this.$emit('change', this.modelValue)
    },
    // todo move it to helper
    openPicker () {
      if (this.pickerState.visibility === 'visible') {
        this.$set(this.pickerState, 'visibility', 'focus')
        this.isDatePickerOpened = false
        return
      }
      let handler = () => {
        this.$set(this.pickerState, 'visibility', 'visible')
        this.isDatePickerOpened = true
        this.$nextTick(this.addClearButton)
        if (this.removeCaret) {
          const removeCaret = () => {
            const caret = this.$refs.picker.$el.querySelector('.vc-popover-caret')
            caret.remove()
          }
          this.$nextTick(removeCaret)
        }
      }
      handler = handler.bind(this)
      setTimeout(handler, 100)
    },
    clearDateListener () {
      if (this.mostRecentOnClear) {
        this.modelValue = this.availableDates[0]
      } else {
        this.modelValue = 'clear'
      }
      this.$emit('change', this.modelValue)
    }
  },
  filters: {
    ...filters
  }
}
</script>

<style scoped lang="scss">

  .base-date-picker {
    &__date_wrapper {
      display: flex;
      flex-direction: column;
      position: relative;
    }

    &__title {
      background-color: transparent;
      margin-right: 30px;
      display: flex;
      padding: 5px 10px;
      border: 1px solid $rfx-light-steel;
      border-radius: .5em;
      align-items: center;
      font-size: 12px;
      color: $rfx-medium-march;
      height: 40px;
      box-sizing: border-box;
      justify-content: space-between;
      min-width: 160px;
      font-weight: 500;
      cursor: pointer;
      white-space: nowrap;

      &_border-less {
        border: none;
      }
    }

    &__overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      padding: 0;
      background: transparent;
    }

    &__arrow {
      flex: 0 0 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      padding-bottom: 3px;
      margin-left: 10px;
      margin-right: 5px;

      &::after {
        content: '';
        display: block;
        width: 7px;
        height: 7px;
        box-sizing: border-box;
        border-right: 2px solid $rfx-bright-blue;
        border-bottom: 2px solid $rfx-bright-blue;
        transform: rotate(45deg);
        transform-origin: center;
      }
    }

    &__date-button {
      outline: none;
      border: none;
      text-align: initial;
      font-size: 14px;
      padding: 0;
      background-color: transparent;
      color: currentColor;
      cursor: pointer;
      margin-top: 8px;
      font-weight: inherit;

      &_highlighted {
        color: $rfx-bright-blue;
      }

      &:hover {
        color: $rfx-bright-blue;
      }

      &_icon {
        align-self: auto;
        font-size: 24px;
      }

      &_disabled {
        pointer-events: none;
        color: $rfx-light-march;

        &:hover {
          color: $rfx-dark-march;
        }
      }
    }

    &__date-picker {
      height: 0;
      top: 0;
      position: absolute;
      left: -56px;

    }

    &__available-date-picker {
      top: 8px !important;
      left: -22px !important;
    }
  }

</style>
